import { FC, useState } from 'react';
import {
    Tooltip,
    styled,
    tooltipClasses,
    typographyClasses,
    TooltipProps,
    useMediaQuery,
    Button,
} from '@mui/material';
import { useTheme } from '@emotion/react';

import { ReactComponent as QuestionIcon } from 'src/assets/svg/circled-question-mark.svg';
import { getTheme } from 'src/utils/getTheme';

import type { CustomTooltipProps } from './types';

const { isHosTheme } = getTheme();

export const StyledTooltip = styled(({ className, placement, ...props }: TooltipProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    return (
        <Tooltip
            {...props}
            placement={isSmallScreen ? 'top' : placement}
            classes={{ popper: className }}
            arrow
            disableHoverListener={isSmallScreen}
        />
    );
})(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.background.paper,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        padding: '15px',
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.up('lg')]: {
            maxWidth: '400px',
        },
        ...(isHosTheme && {
            backgroundColor: theme.palette.common.white,
            border: `0.5px solid ${theme.palette.secondary.dark}`,
            borderRadius: theme.custom.borderRadius[23],

            [`& .${typographyClasses.root}`]: {
                color: theme.palette.secondary.dark,
            },
        }),
    },
}));

export const CustomTooltip: FC<CustomTooltipProps> = ({
    children,
    title,
    placement = 'right',
    arrow = true,
}) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const handleTooltipOpen = () => {
        setOpen(true);
    };

    const handleTooltipClose = () => {
        setOpen(false);
    };

    return (
        <StyledTooltip
            title={title}
            placement={placement}
            arrow={arrow}
            open={isSmallScreen ? open : undefined}
            onOpen={isSmallScreen ? handleTooltipOpen : undefined}
            onClose={handleTooltipClose}
        >
            <Button
                onClick={isSmallScreen ? handleTooltipOpen : undefined}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
                {children || <QuestionIcon width="16px" height="16px" />}
            </Button>
        </StyledTooltip>
    );
};
